import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-arq-bg p-6">
      <div className="w-full max-w-sm arq-fade-up">
        <div className="flex items-center gap-2 mb-10 justify-center">
          <div className="h-8 w-8 bg-arq-green rounded-sm flex items-center justify-center">
            <span className="text-arq-bg font-display font-bold">A</span>
          </div>
          <span className="font-display font-bold text-xl tracking-tight text-arq-ink">ARQITRAK</span>
        </div>
        {done ? (
          <div className="text-center space-y-4">
            <CheckCircle2 className="h-12 w-12 text-arq-green mx-auto" strokeWidth={1.4} />
            <h2 className="font-display text-2xl text-arq-ink">Password set</h2>
            <p className="text-sm text-muted-foreground">Your password has been updated. You can now sign in.</p>
            <Button className="w-full" onClick={() => navigate("/login")} data-testid="go-login">Go to sign in</Button>
          </div>
        ) : !token ? (
          <p className="text-center text-sm text-destructive">Invalid or missing reset link.</p>
        ) : (
          <>
            <h2 className="font-display text-3xl text-arq-ink mb-1">Set your password</h2>
            <p className="text-sm text-muted-foreground mb-8">Create a secure password for your ARQITRAK account.</p>
            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              <div>
                <Label htmlFor="pw">New password</Label>
                <Input id="pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5" data-testid="reset-password" />
              </div>
              <div>
                <Label htmlFor="cpw">Confirm password</Label>
                <Input id="cpw" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="mt-1.5" data-testid="reset-confirm" />
              </div>
              {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
              <Button type="submit" className="w-full" disabled={busy} data-testid="reset-submit">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Set password
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
